import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Chip, { ChipProps } from '@mui/material/Chip';
import Person2Outlined from '@mui/icons-material/Person2Outlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import Diversity3Outlined from '@mui/icons-material/Diversity3Outlined';
import MobileFriendlyOutlined from '@mui/icons-material/MobileFriendlyOutlined';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import AssignmentIndOutlined from '@mui/icons-material/AssignmentIndOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import { Formatter } from '@omnigenbiodata/ui';
import DetailRow from '../DetailRow';

interface ParticipantProfileProps {
  participantStatus: string | null;
  firstName: string | null;
  surname: string | null;
  nhsNumber: string | null;
  sexAtBirth: string | null;
  dob: string | null;
  genderIdentity: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  address1: string | null;
  address2: string | null;
  town: string | null;
  postcode: string | null;
}

interface ProfileSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

const errorStatuses = ['WITHDRAWN', 'DECEASED', 'SUSPENDED'];
const activeStatuses = ['ACTIVE', 'ENROLLED', 'CONSENTED'];

const formatDate = (value: string | null) =>
  value ? Formatter({ input: value, format: 'friendlyDate' }) : 'Not recorded';

function getParticipantStatusColour(status: string | null): ChipProps['color'] {
  if (!status) {
    return 'default';
  }

  if (errorStatuses.includes(status)) {
    return 'error';
  }

  if (activeStatuses.includes(status)) {
    return 'success';
  }

  return 'primary';
}

function renderParticipantStatus(status: string | null) {
  const displayStatus = status || 'UNKNOWN';

  return (
    <Chip
      label={displayStatus}
      color={getParticipantStatusColour(displayStatus)}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function ProfileSection({ title, icon, children }: ProfileSectionProps) {
  return (
    <Box mt={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {icon}

        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function ParticipantProfile({
  participantStatus,
  firstName,
  surname,
  nhsNumber,
  sexAtBirth,
  dob,
  genderIdentity,
  email,
  mobile,
  landline,
  address1,
  address2,
  town,
  postcode,
}: ParticipantProfileProps) {
  const addressLines = [address1, address2, town, postcode].filter((line): line is string => Boolean(line));

  return (
    <Box mb={2}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                flexShrink: 0,
              }}
            >
              <Person2Outlined color="primary" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                Participant Profile
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Participant name, identifiers and contact details
              </Typography>
            </Box>
          </Box>

          <ProfileSection title="Participant Status" icon={<AssignmentIndOutlined color="primary" fontSize="small" />}>
            <DetailRow label="Status" value={renderParticipantStatus(participantStatus)} />
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <ProfileSection title="Name" icon={<AccountCircleOutlined color="primary" fontSize="small" />}>
            <DetailRow label="First Name" value={firstName} />
            <DetailRow label="Surname" value={surname} />
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <ProfileSection title="Participant Identifiers" icon={<BadgeOutlined color="primary" fontSize="small" />}>
            <DetailRow label="NHS Number" value={nhsNumber} />
            <DetailRow label="Sex at Birth" value={sexAtBirth} />
            <DetailRow label="DOB" value={formatDate(dob)} />
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <ProfileSection title="Demographics" icon={<Diversity3Outlined color="primary" fontSize="small" />}>
            <DetailRow label="Gender Identity" value={genderIdentity} />
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <ProfileSection title="Contact Details" icon={<MobileFriendlyOutlined color="primary" fontSize="small" />}>
            <DetailRow label="Email" value={email} />
            <DetailRow label="Mobile" value={mobile} />
            <DetailRow label="Landline" value={landline} />
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <ProfileSection title="Address" icon={<PinDropOutlined color="primary" fontSize="small" />}>
            {addressLines.length > 0 ? (
              <Box component="address" sx={{ fontStyle: 'normal' }}>
                {addressLines.map((line) => (
                  <Typography key={line} variant="body1" component="p" align="left" mb={0.5}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" component="p" color="text.secondary">
                Not recorded
              </Typography>
            )}
          </ProfileSection>
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantProfile;
